import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useTutorial } from '@/contexts/TutorialContext';

// Padding around the spotlighted element, in px.
const HOLE_PADDING = 8;
const CALLOUT_WIDTH = 320;
const CALLOUT_GAP = 14;

// Keep the callout clear of the status bar / Dynamic Island and the home
// indicator: a step whose target fills most of the screen (the transcript)
// otherwise pushes it off the bottom edge.
const safeInset = (name: '--safe-top' | '--safe-bottom'): number => {
  if (typeof window === 'undefined') return 0;
  const raw = window.getComputedStyle(document.documentElement).getPropertyValue(name);
  const value = parseFloat(raw);
  return Number.isFinite(value) ? value : 0;
};

interface SpotlightTarget {
  rect: DOMRect;
  /** The target's own corner radius, in px, so the hole matches its shape. */
  radius: number;
}

// The target's own corner radius, so the cutout follows its silhouette rather
// than boxing it. A pill button punched out as a rectangle leaks four wedges of
// undimmed background around its curve.
const cornerRadius = (el: HTMLElement, rect: DOMRect): number => {
  const raw = window.getComputedStyle(el).borderTopLeftRadius;
  const value = parseFloat(raw);
  if (!Number.isFinite(value)) return 0;
  // Percentage radii resolve against the box; the horizontal axis is close
  // enough for a highlight and avoids parsing the elliptical two-value form.
  return raw.endsWith('%') ? (value / 100) * rect.width : value;
};

// First on-screen match wins. Require the target to have size and intersect the
// viewport so hidden responsive controls cannot capture the spotlight.
const findVisibleTarget = (selector: string | null): SpotlightTarget | null => {
  if (!selector) return null;
  const els = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
  for (const el of els) {
    const rect = el.getBoundingClientRect();
    const hasSize = rect.width > 0 && rect.height > 0;
    const inViewport =
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth;
    if (hasSize && inViewport) return { rect, radius: cornerRadius(el, rect) };
  }
  return null;
};

const targetKey = (target: SpotlightTarget | null): string =>
  target
    ? `${Math.round(target.rect.top)}:${Math.round(target.rect.left)}:${Math.round(target.rect.width)}:${Math.round(target.rect.height)}:${Math.round(target.radius)}`
    : 'none';

const TutorialOverlay: React.FC = () => {
  const { isActive, stepIndex, totalSteps, step, next, back, skip } = useTutorial();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [target, setTarget] = useState<SpotlightTarget | null>(null);
  const calloutRef = useRef<HTMLDivElement>(null);
  const [calloutHeight, setCalloutHeight] = useState(0);

  // The callout is positioned numerically so it can be clamped inside the
  // viewport, which needs its rendered height. Measure it after layout and on
  // any resize (rotation, dynamic type, a step with a longer body).
  useLayoutEffect(() => {
    const el = calloutRef.current;
    if (!el) return;
    const measure = () => setCalloutHeight(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isActive, step?.id]);

  // Re-measure the target as the user navigates between screens and as the
  // layout shifts. A light interval plus scroll/resize listeners keeps the
  // spotlight glued to the element without a render-loop.
  useEffect(() => {
    if (!isActive || !step?.selector) {
      setTarget(null);
      return;
    }

    let prevKey = '';
    const measure = () => {
      const nextTarget = findVisibleTarget(step.selector);
      const key = targetKey(nextTarget);
      if (key !== prevKey) {
        prevKey = key;
        setTarget(nextTarget);
      }
    };

    measure();
    const intervalId = window.setInterval(measure, 250);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [isActive, step?.selector]);

  if (!isActive || !step) return null;

  const isLastStep = stepIndex === totalSteps - 1;
  const dim = 'rgba(0, 0, 0, 0.6)';

  // Panels that swallow taps outside the highlight. They carry no colour of
  // their own — the dim is painted by the single rounded cutout below, which
  // is the only layer that can follow the target's curve.
  const blockerStyle: React.CSSProperties = { position: 'fixed' };

  let mask: React.ReactNode;
  let calloutPosition: React.CSSProperties;

  if (target) {
    const { rect } = target;
    const holeTop = Math.max(0, rect.top - HOLE_PADDING);
    const holeLeft = Math.max(0, rect.left - HOLE_PADDING);
    const holeWidth = rect.width + HOLE_PADDING * 2;
    const holeHeight = rect.height + HOLE_PADDING * 2;
    const holeBottom = holeTop + holeHeight;
    const holeRight = holeLeft + holeWidth;
    // Grow the target's radius by the padding so the cutout stays concentric
    // with it, capped at a full pill for elements that are already one.
    const holeRadius = Math.min(target.radius + HOLE_PADDING, holeWidth / 2, holeHeight / 2);

    mask = (
      <>
        {/* The dim itself: an outward spread on a rounded box, so the hole is
            cut with the same curve as the element it frames. The spread only
            has to reach the far side of the viewport from the hole. */}
        <div
          style={{
            position: 'fixed',
            top: holeTop,
            left: holeLeft,
            width: holeWidth,
            height: holeHeight,
            borderRadius: holeRadius,
            boxShadow: `0 0 0 200vmax ${dim}`,
            pointerEvents: 'none',
          }}
        />
        <div style={{ ...blockerStyle, top: 0, left: 0, right: 0, height: holeTop }} />
        <div style={{ ...blockerStyle, top: holeBottom, left: 0, right: 0, bottom: 0 }} />
        <div style={{ ...blockerStyle, top: holeTop, left: 0, width: holeLeft, height: holeHeight }} />
        <div style={{ ...blockerStyle, top: holeTop, left: holeRight, right: 0, height: holeHeight }} />
        {/* Highlight ring — non-interactive so clicks reach the element. */}
        <div
          style={{
            position: 'fixed',
            top: holeTop,
            left: holeLeft,
            width: holeWidth,
            height: holeHeight,
            border: '2px solid #2563eb',
            borderRadius: holeRadius,
            boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.35)',
            pointerEvents: 'none',
          }}
        />
      </>
    );

    // Place the callout above or below the hole, clamped to the viewport.
    const left = Math.min(
      Math.max(CALLOUT_GAP, rect.left + rect.width / 2 - CALLOUT_WIDTH / 2),
      window.innerWidth - CALLOUT_WIDTH - CALLOUT_GAP,
    );

    // Vertical placement is a preference, not a guarantee: a target that fills
    // the screen leaves no room on the preferred side, so fall back to the
    // other one and, failing that, clamp into the safe area. Better to overlap
    // the spotlight than to run off the edge with the buttons cut off.
    const minTop = CALLOUT_GAP + safeInset('--safe-top');
    const maxTop = window.innerHeight - CALLOUT_GAP - safeInset('--safe-bottom') - calloutHeight;
    const above = holeTop - CALLOUT_GAP - calloutHeight;
    const below = holeBottom + CALLOUT_GAP;
    const fits = (top: number) => top >= minTop && top <= maxTop;

    const preferred = step.placement === 'top' ? above : below;
    const fallback = step.placement === 'top' ? below : above;
    let top = preferred;
    if (!fits(preferred)) {
      top = fits(fallback) ? fallback : Math.min(Math.max(preferred, minTop), Math.max(minTop, maxTop));
    }

    calloutPosition = { left, top };
  } else {
    // No target (intro/outro steps, or the element isn't on screen yet):
    // a single dim layer with a centered callout.
    mask = <div style={{ position: 'fixed', inset: 0, backgroundColor: dim }} />;
    calloutPosition = {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const callout = (
    <div
      ref={calloutRef}
      role="dialog"
      aria-modal="true"
      aria-label={step.title}
      style={{
        position: 'fixed',
        width: CALLOUT_WIDTH,
        maxWidth: '90vw',
        ...calloutPosition,
        backgroundColor: isDarkMode ? '#2d3139' : '#ffffff',
        color: isDarkMode ? '#f3f4f6' : '#1f2937',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
        padding: 20,
        zIndex: 1,
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 6 }}>
        Step {stepIndex + 1} of {totalSteps}
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{step.title}</h3>
      <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 18, opacity: 0.9 }}>{step.body}</p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button
          onClick={skip}
          style={{ fontSize: 14, color: isDarkMode ? '#9ca3af' : '#6b7280', cursor: 'pointer', background: 'none' }}
        >
          Skip
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {stepIndex > 0 && (
            <button
              onClick={back}
              style={{
                fontSize: 14,
                padding: '8px 14px',
                borderRadius: 9999,
                cursor: 'pointer',
                color: isDarkMode ? '#e5e7eb' : '#374151',
                border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                background: 'none',
              }}
            >
              Back
            </button>
          )}
          <button
            onClick={next}
            style={{
              fontSize: 14,
              padding: '8px 16px',
              borderRadius: 9999,
              cursor: 'pointer',
              color: '#ffffff',
              backgroundColor: '#2563eb',
              border: 'none',
            }}
          >
            {isLastStep ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }} aria-live="polite">
      {mask}
      {callout}
    </div>,
    document.body,
  );
};

export default TutorialOverlay;
